import {
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  X,
  type LucideIcon,
} from "lucide-react";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import {
  isIosStandalonePhonePwa,
  isIosStandalonePwa,
  lockIosStandaloneDrawerScroll,
} from "@/lib/iosStandaloneScrollLock";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";
import { Button, IconButton } from "./Button";
import { EmptyState } from "./data";

/**
 * Slide-over drawer — right-anchored, non-blocking detail panel.
 * Animates `right` (not transform) for robustness; scrim dims the page;
 * Esc + scrim-click close; focus is trapped while open and restored on close.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  variant = "default",
  width,
  onWidthChange,
  minWidth = 380,
  maxWidth = 760,
  mobileFullscreen = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** A wider operational workspace on desktop and a native-like screen on phones. */
  variant?: "default" | "workspace";
  /** Desktop width override in px (still capped at 92vw on small screens). */
  width?: number;
  /** When set, a left-edge grip lets the user resize the drawer (desktop). */
  onWidthChange?: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  /** Full-screen, safe-area-aware phone layout WITHOUT the workspace variant's
   *  desktop changes (wider default width, background scroll-lock). For a
   *  default-variant panel that should become a real full-screen chat on
   *  mobile — e.g. the AI assistant. */
  mobileFullscreen?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, open);
  const iosStandaloneInFlow = mobileFullscreen && isIosStandalonePhonePwa();

  const resizeRef = useRef<{ pointerId: number } | null>(null);
  const clampWidth = (value: number) =>
    Math.min(Math.max(value, minWidth), Math.min(maxWidth, window.innerWidth - 48));

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // A dynamically-mounted viewport-sized fixed surface makes iOS standalone
  // drop from the 874px full-bleed window to its 812px layout viewport. The
  // minimal device repro measured the drop 44ms after mount, before focus or
  // keyboard activity. Keep the existing React content alive, hide only the
  // underlying mobile shell, and let the portal participate in normal body
  // flow. No html/body geometry or overflow is changed.
  useLayoutEffect(() => {
    if (!open || !iosStandaloneInFlow) return;
    const shell = document.querySelector<HTMLElement>(".mobile-app");
    if (!shell) return;
    const previous = {
      display: shell.style.display,
      ariaHidden: shell.getAttribute("aria-hidden"),
      inert: shell.hasAttribute("inert"),
      assistantMode: document.body.dataset.traceAssistantMode,
    };
    shell.style.display = "none";
    shell.setAttribute("aria-hidden", "true");
    shell.setAttribute("inert", "");
    document.body.dataset.traceAssistantMode = "in-flow";

    return () => {
      shell.style.display = previous.display;
      if (previous.ariaHidden === null) {
        shell.removeAttribute("aria-hidden");
      } else {
        shell.setAttribute("aria-hidden", previous.ariaHidden);
      }
      if (!previous.inert) shell.removeAttribute("inert");
      if (previous.assistantMode === undefined) {
        delete document.body.dataset.traceAssistantMode;
      } else {
        document.body.dataset.traceAssistantMode = previous.assistantMode;
      }
    };
  }, [open, iosStandaloneInFlow]);

  // Lock the document while a full-screen drawer is open. On iOS standalone,
  // never use position:fixed: with viewport-fit=cover it corrupts the native
  // viewport and leaves a system gap (WebKit 237961). A touch-boundary guard
  // blocks background/rubber-band movement without changing body geometry.
  // Other platforms keep the established fixed-body lock.
  useEffect(() => {
    if (!open || (variant !== "workspace" && !mobileFullscreen)) return;
    if (iosStandaloneInFlow) return;
    if (mobileFullscreen && isIosStandalonePwa() && ref.current) {
      return lockIosStandaloneDrawerScroll(ref.current);
    }

    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open, variant, mobileFullscreen, iosStandaloneInFlow]);

  if (iosStandaloneInFlow) {
    if (!open) return null;
    return createPortal(
      <section
        ref={ref}
        className="assistant-page-ios"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Assistant"}
      >
        <div className="assistant-page-ios__head">
          <span className="assistant-page-ios__title" data-ds-display>
            {title}
          </span>
          <IconButton icon={X} label="Close panel" onClick={onClose} />
        </div>
        <div className="assistant-page-ios__body">{children}</div>
        {footer && <div className="assistant-page-ios__footer">{footer}</div>}
      </section>,
      document.body,
    );
  }

  return createPortal(
    <>
      <div
        className={cn("scrim", open && "scrim--open")}
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        ref={ref}
        className={cn(
          "drawer",
          variant === "workspace" && "drawer--workspace",
          mobileFullscreen && "drawer--mobile-fs",
          open && "drawer--open",
        )}
        style={width ? { width: `min(${width}px, 92vw)` } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Panel"}
        aria-hidden={!open}
        inert={!open}
      >
        {onWidthChange && (
          <div
            className="absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-ew-resize hover:bg-accent/30 md:block"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            onPointerDown={(event) => {
              event.preventDefault();
              resizeRef.current = { pointerId: event.pointerId };
              try {
                event.currentTarget.setPointerCapture(event.pointerId);
              } catch {
                // Capture is an optimisation (drag keeps tracking outside the
                // grip) — a browser that rejects the pointer id still resizes.
              }
            }}
            onPointerMove={(event) => {
              if (resizeRef.current?.pointerId !== event.pointerId) return;
              onWidthChange(clampWidth(window.innerWidth - event.clientX));
            }}
            onPointerUp={(event) => {
              if (resizeRef.current?.pointerId !== event.pointerId) return;
              resizeRef.current = null;
            }}
            onPointerCancel={() => {
              resizeRef.current = null;
            }}
          />
        )}
        <div className="drawer__head">
          {variant === "workspace" && (
            <IconButton
              icon={ArrowLeft}
              label="Back"
              onClick={onClose}
              className="drawer__back"
            />
          )}
          <span className="drawer__title" data-ds-display>
            {title}
          </span>
          <IconButton
            icon={X}
            label="Close panel"
            onClick={onClose}
            className={cn(variant === "workspace" && "drawer__close")}
          />
        </div>
        <div className="drawer__body">{children}</div>
        {footer && (
          <div className="drawer__footer">{footer}</div>
        )}
      </aside>
    </>,
    document.body,
  );
}

/**
 * Centered confirm modal — the blocking decision point for destructive or
 * irreversible actions (replaces window.confirm). While `pending` the dialog
 * stays open showing progress on the confirm button, and Esc / scrim-click /
 * Cancel are inert so an in-flight mutation is never silently orphaned.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  pendingLabel,
  tone = "danger",
  pending = false,
  icon,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel: ReactNode;
  /** Confirm-button label while the action is in flight (e.g. "Deleting…"). */
  pendingLabel?: ReactNode;
  tone?: "danger" | "accent";
  pending?: boolean;
  icon?: LucideIcon;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !pending) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, pending, onCancel]);

  const toneVar = tone === "danger" ? "var(--tr-bad)" : "var(--tr-accent)";

  return createPortal(
    <>
      <div
        className={cn("scrim", open && "scrim--open")}
        aria-hidden="true"
        onClick={pending ? undefined : onCancel}
        style={{ zIndex: 95 }}
      />
      <div
        ref={ref}
        className={cn("modal", open && "modal--open")}
        style={{ zIndex: 100 }}
        role="alertdialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Confirm action"}
        aria-hidden={!open}
      >
        <div className="flex items-start gap-3 p-5">
          {icon && (
            <span
              className="flex h-9 w-9 flex-none items-center justify-center rounded-full"
              style={{
                background: `color-mix(in oklab, ${toneVar} 13%, transparent)`,
                color: toneVar,
              }}
            >
              <Icon icon={icon} size={17} />
            </span>
          )}
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="text-sm font-bold text-ink" data-ds-display>
              {title}
            </div>
            {children != null && (
              <div className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
                {children}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-line-2 px-5 py-3.5">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={tone === "danger" ? "bad" : "accent"}
            size="sm"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {pendingLabel ?? confirmLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}

/**
 * Conflict health pill — persistent festival-health signal in the page header.
 * Summarises by worst severity, pulses when issues exist, opens the drawer.
 */
export function ConflictHealthPill({
  summary,
  count,
  ok = false,
  onClick,
  className,
}: {
  summary?: ReactNode;
  count?: number;
  ok?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  if (ok) {
    return (
      <button
        type="button"
        className={cn("health health--ok", className)}
        onClick={onClick}
        aria-label="Open conflicts, no conflicts"
      >
        <span className="health__dot" />
        <span>No conflicts</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className={cn("health health--bad", className)}
      onClick={onClick}
      aria-label="Open conflicts"
    >
      <span className="health__dot" />
      <span>{summary ?? "Conflicts"}</span>
      {count != null && <span className="health__n">{count}</span>}
    </button>
  );
}

export type ConflictSeverity = "critical" | "warning" | "info";

const SEV_TOKEN: Record<ConflictSeverity, string> = {
  critical: "var(--tr-bad)",
  warning: "var(--tr-warn)",
  info: "var(--tr-info)",
};

export interface ConflictCardProps {
  severity: ConflictSeverity;
  type: ReactNode;
  where?: ReactNode;
  stageColor?: string;
  detail?: ReactNode;
  fixLabel?: ReactNode;
  onFix?: () => void;
}

/** One detected issue inside the Conflicts drawer. Always offer a fix link. */
export function ConflictCard({
  severity,
  type,
  where,
  stageColor,
  detail,
  fixLabel,
  onFix,
}: ConflictCardProps) {
  return (
    <div className="cf-card" style={{ "--sev": SEV_TOKEN[severity] } as CSSProperties}>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="cf-card__sev">{severity}</span>
        <span className="cf-card__type">{type}</span>
      </div>
      {where != null && (
        <div className="cf-card__where">
          {stageColor && (
            <i
              className="inline-block h-2 w-2 rounded-[2px]"
              style={{ background: stageColor }}
            />
          )}
          {where}
        </div>
      )}
      {detail != null && <div className="cf-card__detail mt-1.5">{detail}</div>}
      {onFix && (
        <button type="button" className="cf-card__fix" onClick={onFix}>
          {fixLabel ?? "Fix"}
          <Icon icon={ArrowRight} size={14} />
        </button>
      )}
    </div>
  );
}

export interface ConflictItem extends ConflictCardProps {
  id: string | number;
}

/** Conflicts drawer — composes Drawer + ConflictCard list. */
export function ConflictDrawer({
  open,
  onClose,
  conflicts,
}: {
  open: boolean;
  onClose: () => void;
  conflicts: ConflictItem[];
}) {
  return (
    <Drawer open={open} onClose={onClose} title="Conflicts">
      {conflicts.length === 0 ? (
        <EmptyState
          icon={Check}
          title="No conflicts"
          subtitle="This festival schedule is clear."
        />
      ) : (
        conflicts.map((c) => <ConflictCard key={c.id} {...c} />)
      )}
    </Drawer>
  );
}
